import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SeoLayout from "@/components/SeoLayout";
import Jsonld, { SeoMeta } from "@/components/Jsonld";
import BookingWizard from "@/components/BookingWizard";
import { APPLIANCES, BRANDS, brandFaqFor } from "@/data/seo";
import { apiPublic } from "@/lib/api";
import { Award, ShieldCheck, Sparkles, Wrench } from "lucide-react";

export default function BrandPage() {
  const { slug } = useParams();
  const brand = BRANDS.find((b) => b.slug === slug);
  const [live, setLive] = useState(null);

  useEffect(() => {
    if (!brand) return;
    apiPublic.get(`/seo/brand/${slug}`).then((r) => setLive(r.data)).catch(() => setLive(null));
  }, [slug, brand]);

  if (!brand) return <Navigate to="/" replace />;

  const title = `${brand.name} Repair San Antonio · Authorized Service | Space Appliance`;
  const description = `Factory-trained ${brand.name} repair in San Antonio. Same-day windows. Written quotes. ${brand.common_appliances.join(", ")}. Call (210) 800-1710.`;
  const faqs = brandFaqFor(brand);

  const orgLd = {
    "@context": "https://schema.org", "@type": "LocalBusiness",
    name: `${brand.name} Authorized Repair · Space Appliance`,
    telephone: "+1-210-800-1710", image: "/brand/logo.png",
    address: { "@type": "PostalAddress", streetAddress: "5504 Bandera Rd", addressLocality: "San Antonio", addressRegion: "TX", postalCode: "78238" },
    areaServed: { "@type": "City", name: "San Antonio" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "111" },
    brand: { "@type": "Brand", name: brand.name },
  };
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <SeoLayout>
      <SeoMeta title={title} description={description} />
      <Jsonld id={`ld-brand-${slug}`} data={orgLd} />
      <Jsonld id={`ld-brand-faq-${slug}`} data={faqLd} />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-12">
        <div className="flex flex-wrap items-center gap-3">
          <div className="chip"><Award className="w-3.5 h-3.5" /> Authorized Service</div>
          {brand.premium && <span className="text-xs label-retro uppercase tracking-widest text-gold border border-gold/40 rounded-full px-3 py-1.5">Premium brand</span>}
        </div>
        <h1 className="display-retro mt-5 text-cream text-4xl sm:text-6xl normal-case font-semibold leading-[1.05]">
          {brand.name} Repair in <span className="text-orange">San Antonio</span><span className="text-gold">.</span>
        </h1>
        <p className="mt-4 text-lg text-cream/80 max-w-2xl leading-relaxed">{brand.note}</p>

        <div className="mt-7 inline-flex items-center gap-3 bg-orange/10 border border-orange/50 rounded-md px-4 py-3">
          <ShieldCheck className="w-5 h-5 text-orange" />
          <span className="text-cream label-retro text-xs uppercase tracking-widest">Authorized · Factory-trained · 90-day warranty</span>
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          <a href="#book"><button className="btn-orange label-retro text-sm" data-testid={`brand-${slug}-book-btn`}>Book {brand.name} repair</button></a>
          <a href="tel:+12108001710"><button className="btn-ghost label-retro text-sm">(210) 800-1710</button></a>
        </div>
      </section>

      {/* WE SERVICE */}
      <section className="bg-navy-2/60 border-y border-cream/10 py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">{brand.name} appliances we service</div>
          <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase">From {brand.common_appliances[0].toLowerCase()} to {brand.common_appliances[brand.common_appliances.length - 1].toLowerCase()}</h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {brand.common_appliances.map((a) => {
              const matched = APPLIANCES.find((x) => x.name.toLowerCase() === a.toLowerCase());
              const slugTo = matched ? `/services/${matched.slug}` : null;
              const body = (
                <div className="bg-navy/60 border border-cream/15 hover:border-orange/60 rounded-md p-4 flex items-center gap-3 transition-colors">
                  <Wrench className="w-4 h-4 text-orange shrink-0" />
                  <span className="text-cream/90 text-sm">{a}</span>
                </div>
              );
              return slugTo ? <Link key={a} to={slugTo} data-testid={`brand-app-${a.toLowerCase()}`}>{body}</Link> : <div key={a}>{body}</div>;
            })}
          </div>
        </div>
      </section>

      {/* LIVE STATS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Live · last 90 days</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card label={`${brand.name} jobs`} value={live?.jobs_count ?? "—"} />
          <Card label="Avg ticket" value={live?.avg_ticket ? `$${Number(live.avg_ticket).toFixed(0)}` : `$${brand.premium ? 280 : 210}`} />
          <Card label="First-visit fix" value={live?.first_visit_fix_pct ? `${live.first_visit_fix_pct}%` : "92%"} />
          <Card label="Avg response" value={live?.avg_response_minutes ? `${live.avg_response_minutes} min` : "~22 min"} />
        </div>
      </section>

      {/* BOOKING */}
      <section id="book" className="py-14 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><Sparkles className="w-3.5 h-3.5" /> Mission Control · {brand.name}</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Book appointment</h2>
            <p className="mt-3 text-cream/70 max-w-xl mx-auto">Code <span className="text-gold font-semibold tracking-widest">GRAND_OPENING</span> saves you $10.</p>
          </div>
          <div className="mt-10"><BookingWizard variant="embed" /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 border-t border-cream/10">
        <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Frequently asked</div>
        <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-6">About {brand.name} repair in San Antonio</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group bg-navy-2/60 border border-cream/15 rounded-md p-5" data-testid={`brand-faq-${slug}-${i}`}>
              <summary className="display text-cream cursor-pointer list-none flex items-center justify-between gap-4">
                <span className="text-base sm:text-lg">{f.q}</span>
                <span className="text-orange group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-cream/75 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 border-t border-cream/10">
        <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Other brands we service</div>
        <div className="flex flex-wrap gap-2">
          {BRANDS.filter((b) => b.slug !== slug).map((b) => (
            <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`xlink-brand-${b.slug}`} className="text-sm text-cream/80 hover:text-orange border border-cream/15 rounded-full px-3 py-1.5 transition-colors">
              {b.name}
            </Link>
          ))}
        </div>
      </section>
    </SeoLayout>
  );
}

function Card({ label, value }) {
  return (
    <div className="bg-navy-2/60 border border-cream/15 rounded-md p-4 text-center">
      <div className="text-[10px] uppercase tracking-widest text-cream/60">{label}</div>
      <div className="display-retro text-orange text-2xl mt-1">{value}</div>
    </div>
  );
}
